<script lang="ts">
	import { BROWSER } from 'esm-env'
	import lodash from 'lodash'
	import { onMount } from 'svelte'
	import IntersectionObserver from 'svelte-intersection-observer'
	import { sendGTM } from '$lib/analytics'
	import { HEAT_PUMP } from '$lib/routes'
	import { t } from '$lib/translations'

	export let title: string
	export let packageLimit: number
	export let packages: {
		slugId: string
	}[]

	type Package = {
		brandName?: string
		catalogId?: string
		catalogTotalPrice?: number
		catalogUid?: string
		currencyCode?: string
		description?: string
		deviceSubtypeId?: string
		dynamicPrice?: number
		floorHeatingCop?: string | number
		floorHeatingPerformance?: string | number
		heatPumpSystem?: string
		heatPumpType?: string
		images?: any[]
		languageCode?: string
		maxHeatLoss?: string
		noiseLevel?: number
		organizationId?: string
		originCountry?: string
		radiatorHeatingCop?: number
		radiatorHeatingPerformance?: number
		slugId?: string
		supplierId?: string
		supplierName?: string
		title: string
		withWater?: boolean
		thumbnail?: string
	}

	type AttributeMapping = {
		[key: string]: {
			show?: boolean
			displayName?: string
			unit?: string
			valueMapping?: {
				[key: string]: string
			}
			hintMessage?: string
			order?: number
		}
	}

	type PackageGTM = {
		item_id?: string
		price?: number
		index: number
		item_list_name?: string
		item_brand?: string
		item_name?: string
		item_category2?: string
		heating_power?: number
		item_category?: string
	}

	let packageBoxes: {
		catalogPackages?: Package[]
		totalPackages?: number
	}

	const attributeMapping: AttributeMapping = {
		currencyCode: {
			displayName: t('web.dynamic_pages.components.attributes.currency'),
			valueMapping: {
				CZK: 'Kč',
				EUR: '€',
				PLN: 'zł'
			}
		},
		catalogTotalPrice: {
			displayName: t('web.dynamic_pages.components.attributes.price')
		},
		radiatorHeatingPerformance: {
			show: true,
			displayName: t('web.dynamic_pages.components.attributes.radiatorHeatingPerformance'),
			unit: 'kW',
			hintMessage: t('web.dynamic_pages.components.attributes.radiatorHeatingPerformance'),
			order: 1
		},
		noiseLevel: {
			show: true,
			displayName: t('web.dynamic_pages.components.attributes.noiseLevel'),
			unit: 'dB',
			hintMessage: t('web.dynamic_pages.components.attributes.noiseLevel'),
			order: 2
		},
		heatPumpSystem: {
			show: true,
			displayName: t('web.dynamic_pages.components.attributes.heatPumpSystem'),
			valueMapping: {
				AIR_TO_WATER: t('web.dynamic_pages.components.attributes.heatPumpSystem.AIR_TO_WATER'),
				GROUND_TO_WATER: t(
					'web.dynamic_pages.components.attributes.heatPumpSystem.GROUND_TO_WATER'
				),
				AIR_TO_AIR: t('web.dynamic_pages.components.attributes.heatPumpSystem.AIR_TO_AIR'),
				WATER_TO_WATER: t('web.dynamic_pages.components.attributes.heatPumpSystem.WATER_TO_WATER')
			},
			hintMessage: t('web.dynamic_pages.components.attributes.heatPumpSystem'),
			order: 3
		},
		heatPumpType: {
			show: true,
			displayName: t('web.dynamic_pages.components.attributes.heatPumpType'),
			valueMapping: {
				MONOBLOK: t('web.dynamic_pages.components.attributes.heatPumpType.MONOBLOK'),
				SPLIT: t('web.dynamic_pages.components.attributes.heatPumpType.SPLIT'),
				AIR_TO_AIR: t('web.dynamic_pages.components.attributes.heatPumpSystem.AIR_TO_AIR'),
				WATER_TO_WATER: t('web.dynamic_pages.components.attributes.heatPumpSystem.WATER_TO_WATER')
			},
			hintMessage: t('web.dynamic_pages.components.attributes.heatPumpType'),
			order: 4
		},
		originCountry: {
			show: true,
			displayName: t('web.dynamic_pages.components.attributes.originCountry'),
			order: 5
		},
		withWater: {
			show: true,
			displayName: t('web.dynamic_pages.components.attributes.withWater'),
			order: 6
		}
	}

	function mapAttributes(packageBox: Package) {
		const mappedPackages = Object.entries(packageBox)
			.map(([key, value]) => {
				if (attributeMapping.hasOwnProperty(key) && attributeMapping[key].show === true) {
					return {
						...attributeMapping[key],
						key,
						value,
						...(typeof value === 'string' &&
							attributeMapping[key].valueMapping && {
								value: attributeMapping[key].valueMapping?.[value.toString()] || value
							})
					}
				}
			})
			.filter(Boolean)

		return mappedPackages.sort((a, b) => (a?.order || 0) - (b?.order || 1))
	}

	const prefexes = ['CZ', 'PL', 'DE', 'IT', 'TČ', 'FVE']

	function formatPackageTitle(title: string) {
		const prefix = title.substring(0, title.indexOf(' '))
		const formatted = prefexes.includes(prefix) ? title.replace(prefix, '') : title
		const formattedTitle = formatted.substring(0, 40) + (formatted.length > 40 ? '...' : '')

		return formattedTitle
	}

	// Code bellow sends packages to GTM when they are visible in the viewport.
	// When the first packages are loaded, code starts 2 second timer – after this timer, it sends all displayed packages to GTM inside one event.
	// When the user sees new packages, the timer is started again – all newly displayed packages are sent to GTM in another event.
	// If user scrolls very slowly, the packages can be pushed to GTM one by one – if he sees all packages at once, they are pushed in one event.
	// Every package is pushed to GTM only once per session.

	let boxElements: HTMLElement[] = []
	let isVisible: boolean[] = []
	let maxVisible: number = 0
	let visiblePackages: (Package | null | undefined)[] = []
	let pushedPackages: (Package | null | undefined)[] = []

	const pushToGTM = (data: any) => {
		if (BROWSER && data.length) {
			sendGTM('view_item_list', {}, { ecommerce: { items: [...data] }, _clear: true })
		}
	}

	const { debounce } = lodash

	const pushPackagesToGtm = debounce((visible, pushed) => {
		let packagesToSend: PackageGTM[] = []
		visible.forEach((pkg: Package, index: number) => {
			if (pkg && !pushed.includes(pkg)) {
				packagesToSend.push({
					item_id: pkg?.catalogId,
					price: pkg?.catalogTotalPrice,
					index: index,
					item_list_name: 'Woltair / Recommended',
					item_brand: pkg?.brandName,
					item_name: pkg?.title,
					item_category2: pkg?.originCountry,
					heating_power: pkg?.radiatorHeatingPerformance,
					item_category: 'HVAC'
				})
				pushed.push(pkg)
			}
		})
		pushed = pushed.filter(Boolean)
		pushToGTM(packagesToSend)
	}, 2000)

	$: isVisible.length > maxVisible &&
		((visible) => {
			visiblePackages = visible.map((value, index) => {
				if (value === true) {
					return packageBoxes?.catalogPackages?.[index]
				}
				return null
			})

			if (visiblePackages.filter(Boolean).length > maxVisible)
				maxVisible = visiblePackages.filter(Boolean).length
		})(isVisible)

	$: ((visiblePkgs) => {
		pushPackagesToGtm(visiblePkgs, pushedPackages)
	})(visiblePackages)

	onMount(async () => {
		const response = await fetch('/api/woltadvisor/get-packages', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json'
			},
			body: JSON.stringify({
				limit: packageLimit,
				...(packages?.length && { packages: packages.map((pkg) => pkg.slugId) })
			})
		})
		packageBoxes = await response.json()
	})
</script>

{#if title}
	<h2
		class="unstyled mb-10 mt-0 text-lg font-semibold leading-normal text-w-blue-950 md:text-2xl lg:text-2.5xl"
	>
		{title}
	</h2>
{/if}

<div
	class="hide-scrollbar flex w-screen grid-cols-1 gap-4 overflow-x-scroll px-4 md:grid md:w-full md:max-w-full md:grid-cols-12 md:gap-8"
>
	{#if packageBoxes?.catalogPackages}
		{#each packageBoxes?.catalogPackages as packageBox, i}
			<IntersectionObserver element={boxElements[i]} bind:intersecting={isVisible[i]}>
				<a
					href="/marketplace/{HEAT_PUMP}/{packageBox.slugId}"
					class="link flex w-full min-w-[90%] max-w-96 self-stretch @container xs:min-w-96 md:col-span-6 md:min-w-0 md:max-w-none xl:col-span-4"
					bind:this={boxElements[i]}
				>
					<!-- marketplace/{packageBox.slugId} -->
					<div
						class="flex grid-cols-12 flex-col items-center gap-4 overflow-hidden rounded-3xl bg-neutral-100 @xs:min-h-[278px] @xs:flex-row"
					>
						<div
							class="flex flex-col gap-y-4 self-stretch p-6 pb-0 @xs:basis-[60%] @xs:p-8 @xs:pr-0 @sm:pb-10 @sm:pr-0 xs:py-10 xs:pr-4"
						>
							<h4
								class="unstyled text-base font-semibold text-neutral-900 transition-colors @lg:text-xl"
							>
								{formatPackageTitle(packageBox.title)}
							</h4>
							<div>
								<div class="attributes mb-4 flex flex-wrap gap-x-2 gap-y-2 text-xs">
									{#each mapAttributes(packageBox) as attribute}
										{#if attribute?.value}
											<span class="attribute whitespace-nowrap">
												{#if typeof attribute?.value === 'boolean'}
													{attribute?.displayName}
												{:else}
													{attribute?.value}{attribute?.unit ? ` ${attribute.unit}` : ''}
												{/if}

												{#if attribute.hintMessage}
													<span
														class="hint-wrapper relative ml-1 hidden aspect-square h-full items-center justify-center rounded-full bg-w-red-500 text-[10px] text-white md:flex"
														>?
														<span
															class="hint pointer-events-none absolute -translate-y-6 rounded-sm bg-w-blue-950 px-2 py-1 opacity-0 transition-opacity duration-100"
															>{attribute.hintMessage}</span
														></span
													>
												{/if}</span
											>
										{/if}
									{/each}
								</div>
							</div>
						</div>

						<div
							class="relative mt-auto flex justify-center self-stretch overflow-hidden @xs:mt-0 @xs:basis-[40%]"
						>
							<img
								src={packageBox.thumbnail ||
									packageBox.images?.[0] ||
									'/images/devices/heatpump-generic.webp'}
								alt={packageBox.title}
								class="h-full max-h-48 object-cover object-left px-2 py-4 @xs:max-h-none @xs:px-0"
								loading="lazy"
							/>
						</div>
					</div>
				</a>
			</IntersectionObserver>
		{/each}
	{:else}
		{#each { length: packageLimit } as _}
			<div
				class="flex w-full min-w-[90%] max-w-96 self-stretch rounded-3xl bg-neutral-100 @container xs:min-w-96 md:col-span-6 md:min-w-0 md:max-w-none xl:col-span-4"
			>
				<div class="flex w-2/3 animate-pulse flex-col p-8 py-10">
					<div class="h-5 w-11/12 rounded-lg bg-neutral-300" />
					<div class="mt-2 h-5 w-9/12 rounded-lg bg-neutral-300" />
					<div class="my-4 flex flex-wrap gap-x-3 gap-y-2">
						{#each { length: 4 } as _}
							<div class="mt-1 h-4 w-12 rounded-lg bg-neutral-300" />
						{/each}
					</div>
					<div class="mt-auto h-5 w-7/12 rounded-lg bg-neutral-300" />
					<div class="mt-2 h-4 w-9/12 rounded-lg bg-neutral-300" />
				</div>
				<div class="flex w-1/3 flex-col">
					<div class="my-8 h-full w-full animate-pulse rounded-l-3xl bg-neutral-300" />
				</div>
			</div>
		{/each}
	{/if}
</div>

<style lang="scss">
	.link:hover h4 {
		@apply text-w-red-500;
	}

	.attributes {
		.attribute {
			@apply flex items-center;
		}
		.attribute:not(:last-child)::after {
			@apply ml-2 mr-2 block aspect-square h-1
			rounded-full bg-neutral-300 content-[""];
		}
	}

	.hint-wrapper:hover .hint {
		@apply opacity-100;
	}
</style>
